import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const CONSENT_KEY = 'kh_cookie_consent';
const CONSENT_VERSION = '1'; // bump to re-ask after policy changes

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(choices) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ ...choices, version: CONSENT_VERSION, date: new Date().toISOString() })
  );
}

export function getConsent(category) {
  const c = getStoredConsent();
  if (!c) return false;
  return !!c[category];
}

export default function CookieConsent() {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true, preferences: true };
    storeConsent(all);
    setVisible(false);
  };

  const rejectAll = () => {
    const minimal = { necessary: true, analytics: false, marketing: false, preferences: false };
    storeConsent(minimal);
    setVisible(false);
  };

  const savePreferences = () => {
    storeConsent({ ...choices, necessary: true });
    setVisible(false);
  };

  const toggle = (key) => {
    if (key === 'necessary') return;
    setChoices(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  const de = lang === 'de';

  const categories = [
    {
      key: 'necessary',
      label: de ? 'Notwendig' : 'Essential',
      desc: de
        ? 'Erforderlich für grundlegende Funktionen wie Anmeldung, Warenkorb und Spracheinstellungen. Diese können nicht deaktiviert werden.'
        : 'Required for core functionality like login, cart, and language settings. These cannot be disabled.',
      locked: true,
    },
    {
      key: 'analytics',
      label: de ? 'Analyse' : 'Analytics',
      desc: de
        ? 'Helfen uns zu verstehen, wie Besucher die Website nutzen (Seitenaufrufe, Scrolltiefe). Keine persönlichen Daten werden geteilt.'
        : 'Help us understand how visitors use the site (page views, scroll depth). No personal data is shared.',
    },
    {
      key: 'marketing',
      label: de ? 'Marketing' : 'Marketing',
      desc: de
        ? 'Ermöglichen personalisierte Werbung und Retargeting. Können Daten mit Werbepartnern teilen.'
        : 'Enable personalised ads and retargeting. May share data with advertising partners.',
    },
    {
      key: 'preferences',
      label: de ? 'Präferenzen' : 'Preferences',
      desc: de
        ? 'Speichern Ihre Einstellungen wie Standort, Währung und kürzlich angesehene Produkte für ein besseres Erlebnis.'
        : 'Store your settings like location, currency, and recently viewed products for a better experience.',
    },
  ];

  return (
    <>
      <div className="cc-overlay" onClick={() => {}} />
      <div className={`cc-banner ${showDetails ? 'cc-expanded' : ''}`}>
        {/* Main banner */}
        <div className="cc-main">
          <div className="cc-icon">🍪</div>
          <div className="cc-text">
            <h3 className="cc-title">
              {de ? 'Wir respektieren Ihre Privatsphäre' : 'We respect your privacy'}
            </h3>
            <p className="cc-desc">
              {de
                ? 'Wir verwenden Cookies und ähnliche Technologien, um Ihr Erlebnis zu verbessern, den Datenverkehr zu analysieren und Inhalte zu personalisieren. Sie können Ihre Einstellungen jederzeit anpassen.'
                : 'We use cookies and similar technologies to enhance your experience, analyse traffic, and personalise content. You can adjust your preferences at any time.'}
            </p>
            {!showDetails && (
              <button className="cc-link" onClick={() => setShowDetails(true)}>
                {de ? 'Einstellungen anpassen' : 'Customise preferences'}
              </button>
            )}
          </div>
          <div className="cc-actions">
            <button className="cc-btn cc-accept" onClick={acceptAll}>
              {de ? 'Alle akzeptieren' : 'Accept all'}
            </button>
            <button className="cc-btn cc-reject" onClick={rejectAll}>
              {de ? 'Nur notwendige' : 'Reject all'}
            </button>
          </div>
        </div>

        {/* Detailed preferences */}
        {showDetails && (
          <div className="cc-details">
            <div className="cc-details-header">
              <h4>{de ? 'Cookie-Einstellungen' : 'Cookie preferences'}</h4>
              <button className="cc-close-details" onClick={() => setShowDetails(false)}>×</button>
            </div>
            <div className="cc-categories">
              {categories.map(cat => (
                <div key={cat.key} className="cc-category">
                  <div className="cc-cat-row">
                    <div className="cc-cat-info">
                      <span className="cc-cat-label">{cat.label}</span>
                      <p className="cc-cat-desc">{cat.desc}</p>
                    </div>
                    <label className={`cc-toggle ${cat.locked ? 'locked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={choices[cat.key]}
                        onChange={() => toggle(cat.key)}
                        disabled={cat.locked}
                      />
                      <span className="cc-toggle-slider" />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="cc-details-actions">
              <button className="cc-btn cc-save" onClick={savePreferences}>
                {de ? 'Auswahl speichern' : 'Save preferences'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{cookieStyles}</style>
    </>
  );
}

const cookieStyles = `
/* ─── Cookie Consent Overlay ─── */
.cc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9998;
  backdrop-filter: blur(2px);
  animation: ccFadeIn 0.3s ease;
}

/* ─── Cookie Banner ─── */
.cc-banner {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: calc(100% - 48px);
  max-width: 780px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0,0,0,0.06);
  overflow: hidden;
  animation: ccSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cc-main {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 28px 30px;
}

.cc-icon {
  font-size: 32px;
  flex-shrink: 0;
  margin-top: 2px;
}

.cc-text {
  flex: 1;
  min-width: 0;
}

.cc-title {
  font-family: var(--font-serif, Georgia, serif);
  font-size: 1.1rem;
  font-weight: 500;
  color: #1b1c15;
  margin: 0 0 8px;
}

.cc-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.65;
  margin: 0 0 6px;
}

.cc-link {
  background: none;
  border: none;
  color: var(--accent-gold, #785600);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.cc-link:hover { color: #1b1c15; }

.cc-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.cc-btn {
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-family: inherit;
}

.cc-accept {
  background: linear-gradient(135deg, #1b1c15, #2d2416);
  color: #fff;
}
.cc-accept:hover { opacity: 0.9; transform: translateY(-1px); }

.cc-reject {
  background: #f5f4f0;
  color: #555;
  border: 1px solid #e0ddd5;
}
.cc-reject:hover { background: #eae8e0; }

.cc-save {
  background: var(--accent-gold, #785600);
  color: #fff;
  width: 100%;
  padding: 13px;
}
.cc-save:hover { opacity: 0.9; }

/* ─── Details Panel ─── */
.cc-details {
  border-top: 1px solid #eee;
  padding: 0 30px 24px;
  animation: ccFadeIn 0.25s ease;
}

.cc-details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0 14px;
}
.cc-details-header h4 {
  font-size: 14px;
  font-weight: 600;
  color: #1b1c15;
  margin: 0;
}
.cc-close-details {
  background: none;
  border: none;
  font-size: 22px;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
}
.cc-close-details:hover { color: #333; }

.cc-categories {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cc-category {
  padding: 14px 0;
  border-bottom: 1px solid #f3f2ee;
}
.cc-category:last-child { border-bottom: none; }

.cc-cat-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.cc-cat-info { flex: 1; }

.cc-cat-label {
  font-size: 13px;
  font-weight: 600;
  color: #1b1c15;
  display: block;
  margin-bottom: 4px;
}

.cc-cat-desc {
  font-size: 12px;
  color: #888;
  line-height: 1.5;
  margin: 0;
}

/* Toggle switch */
.cc-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.cc-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cc-toggle-slider {
  position: absolute;
  inset: 0;
  background: #ddd;
  border-radius: 24px;
  cursor: pointer;
  transition: 0.3s;
}

.cc-toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

.cc-toggle input:checked + .cc-toggle-slider {
  background: #1b1c15;
}

.cc-toggle input:checked + .cc-toggle-slider::before {
  transform: translateX(20px);
}

.cc-toggle.locked .cc-toggle-slider {
  background: #1b1c15;
  opacity: 0.5;
  cursor: not-allowed;
}

.cc-toggle.locked input:checked + .cc-toggle-slider::before {
  transform: translateX(20px);
}

.cc-details-actions {
  padding-top: 16px;
}

/* ─── Animations ─── */
@keyframes ccSlideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(30px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

@keyframes ccFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ─── Responsive ─── */
@media (max-width: 640px) {
  .cc-banner {
    bottom: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    border-radius: 16px 16px 0 0;
  }

  @keyframes ccSlideUp {
    from { opacity: 0; transform: translateY(100%); }
    to { opacity: 1; transform: translateY(0); }
  }

  .cc-main {
    flex-direction: column;
    padding: 22px 20px;
    gap: 14px;
  }

  .cc-icon { font-size: 26px; }

  .cc-actions {
    flex-direction: row;
    width: 100%;
  }
  .cc-actions .cc-btn { flex: 1; text-align: center; }
  .cc-details { padding: 0 20px 20px; }
}
`;
